import React, { useEffect, useState } from 'react';
import { TableCell } from '@mui/material';
import { useDispatch } from 'react-redux';

// Components
import Button from '@/main/components/shared/button/button';
import Box from '@/main/components/shared/box';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import BoxGrid from '@/main/components/shared/boxGrid/boxGrid';
import StatusComponent from '@/main/components/shared/status/statusComponent';
import { AreaButton, KOGAreaButtons } from '@/main/components/shared/styles';
import { KOGTableRow } from '@/main/components/shared/boxGrid/styles';
import ResultNotFoundComponent from '@/main/components/shared/resultNotFound/resultNotFoundComponent';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';

// Styles
import { useNavigate } from 'react-router-dom';
import { RoutesConstants } from '@/main/routes/routesConstants';

// Api's
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import {
  getStatusRegister,
  useStatusRegisterReducer
} from '@/main/store/statusRegister';

import {
  getBodyshops,
  resetReducerBodyshops,
  useBodyshopReducer
} from '@/main/store/bodyshop';
import { KeyValue } from '@/main/models/common';
import { BodyShopFilterRequest } from '@/main/models/request/bodyshopRequest';
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import { useForm } from 'react-hook-form';
import {
  getBodyshopBranches,
  resetReducerBodyshopBranches,
  useBodyshopBranchReducer
} from '@/main/store/bodyshopBranch';
import {
  getInsurers,
  resetReducerInsurers,
  useInsurerReducer
} from '@/main/store/insurer';
import { BodyshopDataResponse } from '@/main/models/response';
import { bodyshopApi } from '@/main/api';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import { InsurerFilterRequest } from '@/main/models/request';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import {
  getDefaultMessageResultsFound,
  getDefaultOptionDrop,
  getTranslateByKey,
  isNullUndefined,
  isValueQueryParams,
  removeEmptyValues
} from '@/main/services/utils';
import { userIsAdmin, userIsInsurer } from '@/main/services/utilsUser';
import * as S from './styles';
import useFormQueryParams from '@/main/hooks/useformQueryParams';
import { withQueryparams } from '@/main/components/core/queryParams/queryparams';
import { toast } from 'react-toastify';

export interface ISearchBodyshopForm {
  country: KeyValue | null;
  company: KeyValue | null;
  companyBranch: KeyValue | null;
  rfc: string | null;
  insurer: KeyValue | null;
  statusRegister: KeyValue | null;
}

interface IGridData {
  data: BodyshopDataResponse[];
  totalItems: number;
  isLoading: boolean;
}

interface IPropsInsurerSearchGrid {
  gridData: IGridData;
  formFilters: BodyShopFilterRequest | null;
  setFormFilters: (formFilters: BodyShopFilterRequest | null) => void;
  getGridData: (formFilters: BodyShopFilterRequest) => void;
  hasShowMore: boolean;
  pageIndex: number;
  setPageIndex: (index: number) => void;
  isLoadingInfiniteScroll: boolean;
}

const BodyshopSearchGrid = ({
  gridData,
  formFilters,
  setFormFilters,
  getGridData,
  hasShowMore,
  pageIndex,
  setPageIndex,
  isLoadingInfiniteScroll
}: IPropsInsurerSearchGrid) => {
  const rowHeaders = [
    getTranslateByKey('information.rfc'),
    `${getTranslateByKey('information.company')}/${getTranslateByKey(
      'information.companyBranch'
    )}`,
    getTranslateByKey('information.tradeName'),
    getTranslateByKey('information.insurer'),
    `${getTranslateByKey('information.state')}/${getTranslateByKey(
      'information.city'
    )}`,
    getTranslateByKey('information.status')
  ];
  const titleGrid = getDefaultMessageResultsFound(
    gridData.data.length,
    gridData.totalItems
  );
  const navigate = useNavigate();

  function handleRowBoxGrid(bodyshop: BodyshopDataResponse) {
    if(bodyshop.hasOtherCompanyRelation){
      toast.error( getTranslateByKey('information.companyAssociatedWithAnotherInsurance', true));
      return;
    }
    navigate(
      `${RoutesConstants.registrationRegisterBodyshop}?bodyShopGuid=${bodyshop.guid}`
    );
  }

  function onLoadSearchGrid() {
    const index = pageIndex + PaginationEnum.PageIndexIntial;
    setPageIndex(index);

    const formFiltersOnLoadSearchGrid = formFilters;
    if (formFiltersOnLoadSearchGrid) {
      formFiltersOnLoadSearchGrid.pageIndex = index;

      setFormFilters(formFiltersOnLoadSearchGrid);
      getGridData(formFiltersOnLoadSearchGrid);
    }
  }

  function renderAddress(bodyshop: BodyshopDataResponse) {
    if (bodyshop.addresses && bodyshop.addresses.length > 0) {
      const state = bodyshop.addresses[0]?.municipality?.city?.state?.name;
      const city = bodyshop.addresses[0]?.municipality?.city?.name;
      if (state && city) {
        return `${state}/${city}`;
      }
    }
    return <></>;
  }

  function renderCompanyAndCompanyBranch(bodyshop: BodyshopDataResponse) {
    if (bodyshop.headQuarter) {
      const companyNameHeadQuarter = bodyshop.headQuarter.companyName;
      if (companyNameHeadQuarter) {
        return `${companyNameHeadQuarter}/${bodyshop.companyName}`;
      }
    }
    return bodyshop.companyName;
  }

  if (gridData.isLoading) return <LoadingComponent />;
  if (gridData.data.length === 0 && formFilters !== null)
    return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
  if (gridData.data.length === 0 && !gridData.isLoading) return null;

  return (
    <BoxGrid
      title={titleGrid}
      rowHeaders={rowHeaders}
      infiniteScrollConfig={{
        dataLength: gridData.data.length,
        fetchData: () => onLoadSearchGrid(),
        hasMore: hasShowMore,
        isLoading: isLoadingInfiniteScroll
      }}>
         {gridData.data.map((bodyshop: BodyshopDataResponse) => {
          return (
            <KOGTableRow
              key={bodyshop.guid}
              onClick={() => handleRowBoxGrid(bodyshop)}
            >
              <TableCell width="10%">{bodyshop.registrationNumber}</TableCell>
              <TableCell>
                <div style={{ maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {renderCompanyAndCompanyBranch(bodyshop)}
                </div>
              </TableCell>
              <TableCell>
                <div style={{ maxWidth: 350, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {bodyshop.tradeName}
                </div>
              </TableCell>
              <TableCell width="10%">{bodyshop.insurersCount}</TableCell>
              <TableCell width="20%">{renderAddress(bodyshop)}</TableCell>
              <TableCell width="10%">
                <StatusComponent status={bodyshop?.statusRegister} />
              </TableCell>
            </KOGTableRow>
          );
        })}
    </BoxGrid>
  );
};

function BodyshopScreen() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // State & reducers
  const countryReducer = useCountryReducer();
  const statusRegisterReducer = useStatusRegisterReducer();
  const bodyshopReducer = useBodyshopReducer();
  const bdyshopBranchReducer = useBodyshopBranchReducer();
  const insurerReducer = useInsurerReducer();

  const optionDefaultCountryDrop =
    countryReducer.countriesUser.data !== null &&
      countryReducer.countriesUser.data.length === 1
      ? countryReducer.countriesUser.data[0]
      : null;
  const optionDefaultBodyshopDrop =
    bodyshopReducer.bodyshops.data !== null &&
      bodyshopReducer.bodyshops.data.length === 1
      ? bodyshopReducer.bodyshops.data[0]
      : null;
  const optionDefaultBodyshopBranchDrop = getDefaultOptionDrop(
    bdyshopBranchReducer.bodyshopBranches.data
  );
  const INITIAL_FORM_STATE: ISearchBodyshopForm = {
    country: optionDefaultCountryDrop,
    company: optionDefaultBodyshopDrop,
    companyBranch: null,
    insurer: null,
    rfc: '',
    statusRegister: null
  };
  const {
    control,
    customHandleSubmit,
    setValue,
    formState: { errors },
    queryParamsValues,
    reset,
    getValues
  } = useFormQueryParams<ISearchBodyshopForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE,
    onSubmit: onSubmit,
    mapDefaultValues: {
      company: bodyshopReducer.bodyshops,
      companyBranch: bdyshopBranchReducer.bodyshopBranches,
      country: countryReducer.countriesUser,
      insurer: insurerReducer.insurers,
      statusRegister: statusRegisterReducer.statusRegister
    }
  });
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const INITIAL_GRID_STATE: IGridData = {
    totalItems: 0,
    isLoading: false,
    data: []
  };
  const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);
  const [formFilters, setFormFilters] = useState<BodyShopFilterRequest | null>(
    null
  );
  const [hasShowMore, setHasShowMore] = useState<boolean>(false);
  const [pageIndex, setPageIndex] = useState<number>(
    PaginationEnum.PageIndexIntial
  );
  const [isLoadingInfiniteScroll, setIsLoadingInfiniteScroll] = useState<
    boolean
  >(false);

  const isDisabledBodyshopDrop = () =>
    bodyshopReducer.bodyshops.data === null || getValues().country === null;
  const isDisabledBodyshopBranchDrop = () =>
    bdyshopBranchReducer.bodyshopBranches.data === null ||
    getValues().company === null ||
    optionDefaultBodyshopBranchDrop !== null;
  const isDisabledInsurersDrop = () => {
    return userIsInsurer()  || insurerReducer.insurers.data === null || getValues().country === null;;
  };

  // UseEffect's
  // Initial load
  useEffect(() => {
    dispatch(getCountriesUser());
    dispatch(getStatusRegister(true));

    return () => {
      dispatch(resetReducerBodyshops());
      dispatch(resetReducerBodyshopBranches());
      dispatch(resetReducerInsurers());
    };
  }, []);

  useEffect(() => {
    // Call API's related by default country
    if (optionDefaultCountryDrop) {
      callInsurerApiDomain(optionDefaultCountryDrop, null);
    }
  }, [optionDefaultCountryDrop?.key]);

  useEffect(() => {
    if (optionDefaultCountryDrop && insurerReducer.insurers.data && !getValues().company) {
      callBodyshopApiDomain(optionDefaultCountryDrop);
    }
  }, [optionDefaultCountryDrop?.key, insurerReducer.insurers.data]);

  useEffect(() => {
    // Call API's related by default country and bodyshop
    if (optionDefaultCountryDrop && optionDefaultBodyshopDrop) {
      callBodyshopBranchApiDomain(
        optionDefaultCountryDrop,
        optionDefaultBodyshopDrop
      );
    }
  }, [optionDefaultCountryDrop?.key, optionDefaultBodyshopDrop?.key]);

  useEffect(() => {
    const country = queryParamsValues.country ?? null;
    const company = queryParamsValues.company ?? null;
    if (country) {
      callInsurerApiDomain(country, company);
    }
  }, [JSON.stringify(queryParamsValues)]);

  useEffect(() => {
    const country = queryParamsValues.country ?? null;
    const company = queryParamsValues.company ?? null;
    if (country && insurerReducer.insurers.data) {
      callBodyshopApiDomain(country);
      callBodyshopBranchApiDomain(country, company);
    }
  }, [insurerReducer.insurers.data]);

  useEffect(() => {
  if (userIsInsurer() && insurerReducer.insurers.data) {
    const insurer = insurerReducer.insurers.data[0];
    if (insurer) {
      setValue("insurer", insurer);
    }
  }
  }, [insurerReducer.insurers.data]);


  function handleFilters() {
    setShowFilters(!showFilters);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  function handleNew() {
    navigate(RoutesConstants.registrationRegisterBodyshop);
  }

  function handleClear() {
    reset(INITIAL_FORM_STATE);
    if (statusRegisterReducer.statusRegister.data !== null) {
      const keyStatusRegister = String(StatusRegisterEnum.Active);
      const valueStatusRegister = String(
        statusRegisterReducer.statusRegister.data?.find(
          x => x.key === keyStatusRegister
        )?.value
      );
      setValue('statusRegister', {
        key: keyStatusRegister,
        value: valueStatusRegister
      });
    }
    setGridData(INITIAL_GRID_STATE);
    setFormFilters(null);
    setPageIndex(PaginationEnum.PageIndexIntial);
    setHasShowMore(false);
    setIsLoadingInfiniteScroll(false);

    if (optionDefaultCountryDrop !== null) {
      callBodyshopApiDomain(optionDefaultCountryDrop);
      callInsurerApiDomain(optionDefaultCountryDrop, null);

      if (optionDefaultBodyshopDrop !== null) {
        callBodyshopBranchApiDomain(
          optionDefaultCountryDrop,
          optionDefaultBodyshopDrop
        );
      }
    } else {
      dispatch(resetReducerBodyshops());
      dispatch(resetReducerBodyshopBranches());
      dispatch(resetReducerInsurers());
    }
  }

  function callBodyshopApiDomain(optionSelected: KeyValue | null) {
    const isCountryQueryParams = !isNullUndefined(optionSelected) && isValueQueryParams(optionSelected);
    // Clean fields & reducers if country and company not query params
    if (!isCountryQueryParams) {
      setValue('company', null);
      setValue('companyBranch', null);
      dispatch(resetReducerBodyshops());
      dispatch(resetReducerBodyshopBranches());
    }

    if (optionSelected !== null) {
      const request: BodyShopFilterRequest = {
        guidCountry: String(optionSelected.key),
        type: HeadQuarterBranchFilterEnum.Only_Headquarters,
        allActiveStatus: true
      };
      if (userIsInsurer() && insurerReducer.insurers.data) {
        request.guidInsurer = insurerReducer.insurers.data[0].key.toString();
      }
      dispatch(getBodyshops(request));
    }
  }

  function callInsurerApiDomain(
    countrySelected: KeyValue | null,
    companySelected: KeyValue | null
  ) {
    const isCountryQueryParams = !isNullUndefined(countrySelected) && isValueQueryParams(countrySelected);
    const isCompanyQueryParams = !isNullUndefined(companySelected) && isValueQueryParams(companySelected);
    // Clean fields & reducers if country and company not query params
    if (!isCountryQueryParams && !isCompanyQueryParams) {
      setValue('insurer', null);
      dispatch(resetReducerInsurers());
    }

    if (countrySelected !== null) {
      const request: InsurerFilterRequest = {
        guidCountry: String(countrySelected.key),
        type: HeadQuarterBranchFilterEnum.Only_Headquarters
      };

      if (companySelected !== null) {
        request.guidBodyShop = String(companySelected.key);
      }

      dispatch(getInsurers(request));
    }
  }

  function callBodyshopBranchApiDomain(
    countrySelected: KeyValue | null,
    companySelected: KeyValue | null
  ) {
    dispatch(resetReducerBodyshopBranches());
    // Clean fields & reducers
    if (!isValueQueryParams(countrySelected) || !isValueQueryParams(companySelected)) {
      setValue('companyBranch', null);
    }

    if (countrySelected !== null) {
      const request: BodyShopFilterRequest = {
        guidCountry: String(countrySelected.key),
        type: HeadQuarterBranchFilterEnum.All,
        allActiveStatus: true
      };
      if (companySelected) {
        request.guidBodyShopHeadQuarter = String(companySelected.key);
      }
      if (userIsInsurer() && insurerReducer.insurers.data) {
        request.guidInsurer = insurerReducer.insurers.data[0].key.toString();
      }
      dispatch(getBodyshopBranches(request));
    }
  }

  function onSubmit(formInputs: ISearchBodyshopForm) {
    setPageIndex(PaginationEnum.PageIndexIntial);
    setGridData({
      isLoading: true,
      totalItems: 0,
      data: []
    });

    const requestForm: BodyShopFilterRequest = {
      guidCountry: String(formInputs.country?.key),
      guidBodyShop: formInputs.companyBranch?.key
        ? String(formInputs.companyBranch.key)
        : null,
      guidBodyShopHeadQuarter: formInputs.company?.key
        ? String(formInputs.company.key)
        : null,
      registrationNumber: formInputs.rfc,
      idStatusRegister: formInputs.statusRegister?.key
        ? Number(formInputs.statusRegister.key)
        : null,
      guidInsurer: formInputs.insurer?.key
        ? String(formInputs.insurer.key)
        : null,
      type: HeadQuarterBranchFilterEnum.All,
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: PaginationEnum.PageSize30
    };

    setFormFilters(requestForm);
    const requestFormatted = removeEmptyValues(requestForm);

    bodyshopApi.getSearchGrid(requestFormatted).then(response => {
      setGridData({
        isLoading: false,
        totalItems: response.data.totalNumberOfRecords,
        data: response.data.results
      });
      setHasShowMore(
        response.data.results.length < response.data.totalNumberOfRecords
      );
    });
    setShowFilters(!showFilters);
  }

  function onLoadInfiniteScroll(request: BodyShopFilterRequest) {
    setIsLoadingInfiniteScroll(true);
    bodyshopApi
      .getSearchGrid(request)
      .then(response => {
        let resultData: BodyshopDataResponse[] = [];
        let resultTotalItems: number = 0;
        if (
          response.data &&
          response.data.results &&
          response.data.results.length > 0
        ) {
          resultData = response.data.results;
          resultTotalItems = response.data.totalNumberOfRecords;
        }
        if (gridData.data.length > 0) {
          resultData = [...gridData.data, ...resultData];
        }
        setGridData({
          isLoading: false,
          totalItems: resultTotalItems,
          data: resultData
        });

        // Disable show more
        if (
          resultTotalItems < PaginationEnum.PageSize30 ||
          resultTotalItems === resultData.length
        ) {
          setHasShowMore(false);
        }
      })
      .finally(() => setIsLoadingInfiniteScroll(false));
  }

  const canCreateNew = userIsAdmin() || userIsInsurer();
  return (
    <ContainerScreen
      hasBackground
      title={`
      ${getTranslateByKey('information.registration')}
      ${' > '}
      ${getTranslateByKey('information.searchBodyshop')}`}>
      <S.Container>
        <AreaButton>
          <Button onClick={handleFilters} id="id-searchBodyshop-btn-filters">
            {getTranslateByKey('information.filters')}
          </Button>
          {canCreateNew && (
            <Button onClick={handleNew} id="id-searchBodyshop-btn-newBodyshop">
              {getTranslateByKey('information.newBodyshop')}
            </Button>
          )}
        </AreaButton>
        <Box
          showBox={showFilters}
          title={getTranslateByKey('information.filters')}>
          <form onSubmit={customHandleSubmit} noValidate>
            <S.RowInputs>
              <KogAutoCompleteControlled
                required
                label={getTranslateByKey('information.country')}
                control={control}
                options={countryReducer.countriesUser.data}
                isLoading={countryReducer.countriesUser.isLoading}
                nameCustom="country"
                rulesCustom={{ required: true }}
                errorMessageCustom={
                  errors.country?.type === 'required'
                    ? getTranslateByKey('information.countryRequired', true)
                    : ''
                }
                hasErrorCustom={!!errors.country}
                onChangeCustom={(optionSelected: KeyValue | null) => {
                  callBodyshopApiDomain(optionSelected);
                  callInsurerApiDomain(optionSelected, null);
                }}
                autoHighlightCustom={!!optionDefaultCountryDrop}
                setValueCustom={setValue}
                disabled={optionDefaultCountryDrop !== null}
                id="id-searchBodyshop-txt-country"
              />
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.company')}
                control={control}
                nameCustom="company"
                rulesCustom={{ required: false }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={bodyshopReducer.bodyshops.data}
                isLoading={bodyshopReducer.bodyshops.isLoading}
                onChangeCustom={(optionSelected: KeyValue | null) => {
                  callBodyshopBranchApiDomain(
                    getValues().country,
                    optionSelected
                  );
                  if (!userIsInsurer()) {
                    callInsurerApiDomain(getValues().country, optionSelected);
                  }
                }}
                disabled={
                  isDisabledBodyshopDrop() || optionDefaultBodyshopDrop !== null
                }
                autoHighlightCustom={!!optionDefaultBodyshopDrop}
                setValueCustom={setValue}
                id="id-searchBodyshop-txt-company"
              />
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.companyBranch')}
                control={control}
                nameCustom="companyBranch"
                rulesCustom={{ required: false }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={bdyshopBranchReducer.bodyshopBranches.data}
                isLoading={bdyshopBranchReducer.bodyshopBranches.isLoading}
                onChangeCustom={() => { }}
                disabled={isDisabledBodyshopBranchDrop()}
                autoHighlightCustom={!!optionDefaultBodyshopBranchDrop}
                setValueCustom={setValue}
                id="id-searchBodyshop-txt-companyBranch"
              />
            </S.RowInputs>
            <S.RowInputs>
              <KogInputTextControlledNew
                label={getTranslateByKey('information.rfc')}
                helperText={
                  errors.rfc?.type === 'minLength'
                    ? getTranslateByKey(
                      'information.rfcMustHaveAtLeast12Digits',
                      true
                    )
                    : ''
                }
                name="rfc"
                error={!!errors.rfc}
                inputProps={{ maxLength: 20 }}
                controllerProps={{ rules: { minLength: 12 } }}
                control={control}
                id="id-searchBodyshop-txt-rfc"
              />
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.insurer')}
                control={control}
                nameCustom="insurer"
                rulesCustom={{ required: false }}
                errorMessageCustom=""
                hasErrorCustom={false}
                options={insurerReducer.insurers.data}
                isLoading={insurerReducer.insurers.isLoading}
                onChangeCustom={() => { }}
                disabled={isDisabledInsurersDrop()}
                id="id-searchBodyshop-txt-insurer"
              />
              <KogAutoCompleteControlled
                label={getTranslateByKey('information.status')}
                nameCustom="statusRegister"
                valueDefaultKey={String(StatusRegisterEnum.Active)}
                errorMessageCustom=""
                control={control}
                setValueCustom={setValue}
                options={statusRegisterReducer.statusRegister.data}
                isLoading={statusRegisterReducer.statusRegister.isLoading}
                rulesCustom={{ required: false }}
                hasErrorCustom={false}
                onChangeCustom={() => { }}
                id="id-searchBodyshop-txt-statusRegister"
              />
            </S.RowInputs>
            <S.RowInputs>
            </S.RowInputs>
            <KOGAreaButtons>
              <SecondaryButton
                type="submit"
                id="id-searchBodyshop-btn-search">
                {getTranslateByKey('information.search')}
              </SecondaryButton>
              <SecondaryButton
                onClick={handleClear}
                id="id-searchBodyshop-btn-clear">
                {getTranslateByKey('information.clear')}
              </SecondaryButton>
            </KOGAreaButtons>
          </form>
        </Box>

        <BodyshopSearchGrid
          isLoadingInfiniteScroll={isLoadingInfiniteScroll}
          pageIndex={pageIndex}
          setPageIndex={setPageIndex}
          hasShowMore={hasShowMore}
          gridData={gridData}
          formFilters={formFilters}
          setFormFilters={setFormFilters}
          getGridData={(request: BodyShopFilterRequest) => {
            onLoadInfiniteScroll(request);
          }}
        />
      </S.Container>
    </ContainerScreen>
  );
}

export default withQueryparams(BodyshopScreen);
